/**
 * Comprehensive fallback handlers for Vibeship DevTools API routes
 * This file provides graceful degradation when dependencies are missing
 */

import { ApiError, ErrorCodes, ErrorFactory } from './api-errors';

export interface FallbackConfig {
  scanPaths: string[];
  include: string[];
  exclude: string[];
  taskPatterns: {
    todo: boolean;
    fixme: boolean;
    hack: boolean;
    note: boolean;
    bug: boolean;
    optimize: boolean;
    refactor: boolean;
  };
  defaultPhase: string;
  defaultMilestone: string;
  cacheEnabled: boolean;
  cacheTTL: number;
  maxFileSize: number;
  maxDepth: number;
  features: {
    tasks: boolean;
    ai: boolean;
    realtime: boolean;
    commandPalette: boolean;
  };
  security: {
    authentication: boolean;
    rateLimit: boolean;
    cors: boolean;
    allowedPaths: string[];
  };
  ui: {
    theme: string;
    position: string;
    defaultSize: {
      width: string;
      height: string;
    };
    hotkey: string;
    showInProduction: boolean;
  };
  api: {
    basePath: string;
    version: string;
    documentation: boolean;
    timeout: number;
  };
  cache: {
    enabled: boolean;
    directory: string;
    maxSize: number;
    ttl: number;
    strategy: string;
  };
}

/**
 * Get default configuration that works out of the box
 */
export function getDefaultConfig(): FallbackConfig {
  return {
    scanPaths: ['./src', './app', './docs', './README.md', './.tickets'],
    include: ['**/*.md', '**/*.mdx', '**/*.tsx', '**/*.ts', '**/*.jsx', '**/*.js'],
    exclude: ['**/node_modules/**', '**/.next/**', '**/dist/**', '**/build/**'],
    taskPatterns: {
      todo: true,
      fixme: true,
      hack: true,
      note: true,
      bug: true,
      optimize: true,
      refactor: true,
    },
    defaultPhase: 'Backlog',
    defaultMilestone: 'Unassigned',
    cacheEnabled: true,
    cacheTTL: 300000, // 5 minutes
    maxFileSize: 1048576, // 1MB
    maxDepth: 5,
    features: {
      tasks: true,
      ai: false, // Disabled by default for safety
      realtime: true,
      commandPalette: true,
    },
    security: {
      authentication: false,
      rateLimit: false,
      cors: false,
      allowedPaths: ['**/*'],
    },
    ui: {
      theme: 'system',
      position: 'bottom-right',
      defaultSize: {
        width: '400px',
        height: '600px',
      },
      hotkey: 'cmd+k',
      showInProduction: false,
    },
    api: {
      basePath: '/api/vibeship',
      version: 'v1',
      documentation: true,
      timeout: 30000,
    },
    cache: {
      enabled: true,
      directory: '.vibecode/cache',
      maxSize: 100,
      ttl: 300000,
      strategy: 'lru',
    },
  };
}

/**
 * Safely load configuration with multiple fallback strategies
 */
export async function loadConfigSafely(requestId?: string): Promise<FallbackConfig> {
  const attempts: Array<{ name: string; error?: string }> = [];

  // Strategy 1: Try to load from the generated config helper
  try {
    // @ts-ignore - User project file that may not exist
    const { getConfig } = await import('@/lib/vibeship-config');
    const config = await getConfig();
    return config as FallbackConfig;
  } catch (error) {
    attempts.push({
      name: 'Generated config helper',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Strategy 2: Try to load from the root config file
  try {
    // @ts-ignore - User project file that may not exist
    const config = await import('@/vibeship.config');
    return (config.default || config) as FallbackConfig;
  } catch (error) {
    attempts.push({
      name: 'Root config file',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Strategy 3: Try to load from package.json vibeship field
  try {
    // @ts-ignore - User project file that may not exist
    const packageJson = await import('@/package.json');
    if (packageJson.vibeship) {
      const defaultConfig = getDefaultConfig();
      return { ...defaultConfig, ...packageJson.vibeship };
    }
  } catch (error) {
    attempts.push({
      name: 'Package.json config',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Strategy 4: Check environment variables
  try {
    const envConfig = loadConfigFromEnv();
    if (envConfig) {
      const defaultConfig = getDefaultConfig();
      return { ...defaultConfig, ...envConfig };
    }
  } catch (error) {
    attempts.push({
      name: 'Environment variables',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Log all failed attempts in development
  if (process.env.NODE_ENV === 'development') {
    console.warn('⚠️ Vibeship DevTools: All config loading strategies failed:');
    attempts.forEach(({ name, error }) => {
      console.warn(`  - ${name}: ${error}`);
    });
    console.warn('  - Using fallback configuration');
    console.warn('  - Run "npx vibeship init" to generate proper configuration');
  }

  // Return default config as final fallback
  return getDefaultConfig();
}

/**
 * Load configuration from environment variables
 */
function loadConfigFromEnv(): Partial<FallbackConfig> | null {
  const envConfig: Partial<FallbackConfig> = {};

  if (process.env.VIBESHIP_SCAN_PATHS) {
    envConfig.scanPaths = process.env.VIBESHIP_SCAN_PATHS.split(',').map(p => p.trim());
  }

  if (process.env.VIBESHIP_INCLUDE_PATTERNS) {
    envConfig.include = process.env.VIBESHIP_INCLUDE_PATTERNS.split(',').map(p => p.trim());
  }

  if (process.env.VIBESHIP_EXCLUDE_PATTERNS) {
    envConfig.exclude = process.env.VIBESHIP_EXCLUDE_PATTERNS.split(',').map(p => p.trim());
  }

  if (process.env.VIBESHIP_DISABLE_CACHE === 'true') {
    envConfig.cacheEnabled = false;
  }

  if (process.env.VIBESHIP_DISABLE_AI === 'true') {
    envConfig.features = { ...getDefaultConfig().features, ai: false };
  }

  // Return null if no env config found
  return Object.keys(envConfig).length > 0 ? envConfig : null;
}

/**
 * Safe dependency loading with fallbacks
 */
export async function safeDependencyLoad<T>(
  importFn: () => Promise<T>,
  fallbackFn: () => T,
  dependencyName: string,
  requestId?: string
): Promise<T> {
  try {
    return await importFn();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`⚠️ Vibeship DevTools: ${dependencyName} not available, using fallback`);
      console.warn(`   Error: ${error instanceof Error ? error.message : String(error)}`);
    }
    return fallbackFn();
  }
}

/**
 * Create a mock FileScanner for when the real one isn't available
 */
export function createMockFileScanner(config: FallbackConfig) {
  return {
    scan: async (path?: string) => {
      // Return empty array when scanner is unavailable
      return [];
    },
    readFile: async (path: string) => {
      throw new ApiError(
        ErrorCodes.DEPENDENCY_MISSING,
        500,
        {
          message: 'File scanner not available',
          cause: 'FileScanner dependency could not be loaded',
          solution: 'Ensure @vibeship/devtools server package is properly installed',
          docs: 'https://vibeship.dev/docs/troubleshooting#dependency-missing',
        }
      );
    },
    writeFile: async (path: string, content: string) => {
      throw new ApiError(
        ErrorCodes.DEPENDENCY_MISSING,
        500,
        {
          message: 'File operations not available',
          cause: 'FileScanner dependency could not be loaded',
          solution: 'Ensure @vibeship/devtools server package is properly installed',
          docs: 'https://vibeship.dev/docs/troubleshooting#dependency-missing',
        }
      );
    },
    getFileInfo: async (path: string) => {
      throw new ApiError(
        ErrorCodes.DEPENDENCY_MISSING,
        500,
        {
          message: 'File operations not available',
          cause: 'FileScanner dependency could not be loaded',
          solution: 'Ensure @vibeship/devtools server package is properly installed',
          docs: 'https://vibeship.dev/docs/troubleshooting#dependency-missing',
        }
      );
    },
  };
}

/**
 * Create a mock TaskExtractor for when the real one isn't available
 */
export function createMockTaskExtractor() {
  return {
    extract: async (content: string, filePath: string) => {
      // Basic regex-based task extraction as fallback
      const tasks = [];
      const lines = content.split('\n');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const todoMatch = line.match(/(?:\/\/|\/\*|<!--)\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):?\s*(.+?)(?:\*\/|-->)?$/i);
        
        if (todoMatch) {
          tasks.push({
            id: `fallback_${filePath}_${i}`,
            type: todoMatch[1].toLowerCase(),
            content: todoMatch[2].trim(),
            file: filePath,
            line: i + 1,
            priority: 'medium',
            phase: 'Backlog',
            milestone: 'Unassigned',
            tags: [],
            created: new Date().toISOString(),
          });
        }
      }
      
      return tasks;
    },
  };
}

/**
 * Create a mock Logger for when the real one isn't available
 */
export function createMockLogger(prefix: string = 'Vibeship') {
  const logger = {
    info: (message: string, ...args: any[]) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${prefix}] ${message}`, ...args);
      }
    },
    warn: (message: string, ...args: any[]) => {
      console.warn(`[${prefix}] ${message}`, ...args);
    },
    error: (message: string, ...args: any[]) => {
      console.error(`[${prefix}] ${message}`, ...args);
    },
    debug: (message: string, ...args: any[]) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[${prefix}] ${message}`, ...args);
      }
    },
  };

  return logger;
}

/**
 * Create a mock CacheManager for when the real one isn't available
 */
export function createMockCacheManager() {
  const cache = new Map();
  
  return {
    get: (key: string) => cache.get(key),
    set: (key: string, value: any, ttl?: number) => {
      cache.set(key, value);
      // Simple TTL simulation
      if (ttl) {
        setTimeout(() => cache.delete(key), ttl);
      }
    },
    delete: (key: string) => cache.delete(key),
    clear: () => cache.clear(),
    has: (key: string) => cache.has(key),
    size: () => cache.size,
  };
}

/**
 * Comprehensive dependency loader with fallbacks
 */
export async function loadDependenciesWithFallbacks(requestId?: string) {
  const dependencies = {
    config: await loadConfigSafely(requestId),
    FileScanner: null as any,
    TaskExtractor: null as any,
    Logger: null as any,
    CacheManager: null as any,
  };

  // Load FileScanner with fallback
  dependencies.FileScanner = await safeDependencyLoad(
    async () => {
      const { FileScanner } = await import('../../server/index');
      return FileScanner;
    },
    () => createMockFileScanner as any,
    'FileScanner',
    requestId
  );

  // Load TaskExtractor with fallback
  dependencies.TaskExtractor = await safeDependencyLoad(
    async () => {
      const { TaskExtractor } = await import('../../server/index');
      return TaskExtractor;
    },
    () => createMockTaskExtractor as any,
    'TaskExtractor',
    requestId
  );

  // Load Logger with fallback
  dependencies.Logger = await safeDependencyLoad(
    async () => {
      const { Logger } = await import('../../server/index');
      return Logger;
    },
    () => createMockLogger as any,
    'Logger',
    requestId
  );

  // Load CacheManager with fallback
  dependencies.CacheManager = await safeDependencyLoad(
    async () => {
      const { CacheManager } = await import('../../server/index');
      return CacheManager;
    },
    () => createMockCacheManager as any,
    'CacheManager',
    requestId
  );

  return dependencies;
}

/**
 * Health check for dependencies
 */
export async function checkDependencyHealth(): Promise<{
  healthy: boolean;
  missing: string[];
  available: string[];
}> {
  const checks = [
    { name: 'ServerPackage', test: () => import('../../server/index') },
  ];

  const results = await Promise.allSettled(
    checks.map(async ({ name, test }) => {
      try {
        await test();
        return { name, available: true };
      } catch {
        return { name, available: false };
      }
    })
  );

  const available: string[] = [];
  const missing: string[] = [];

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      if (result.value.available) {
        available.push(result.value.name);
      } else {
        missing.push(result.value.name);
      }
    }
  });

  return {
    healthy: missing.length === 0,
    missing,
    available,
  };
}